import React from 'react';

import StatusIconCDS from 'app/shared/componentsRestyled/coreDesignSystem/StatusIconCDS';
import dayjs from 'dayjs';
import _ from 'lodash';

import { CHECKLIST_RECALCULATING_STATUSES } from 'constants/checklists';
import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconSize } from 'constants/coreDesignSystem';
import { SortOrder } from 'constants/data';
import { COMMON_DATE_FORMAT } from 'constants/date';
import { CONFLICT_TYPE, HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import {
  LoanNavigationConfig,
  OccupancyType,
  PropertyMortgageType,
  PropertyStatusType,
  PropertyType,
  RentCalculatedPurposeType,
  TypeOfCredit,
} from 'constants/loanApplication';
import { HistoryPendingChangesActionType } from 'constants/pendingChanges';
import { StatusEnum } from 'constants/status';
import { getDefaultErrorMessage } from 'utils/axios';
import { getChecklistResultStatus } from 'utils/checklist';
import { getConditionalArray, getConditionalObject } from 'utils/data';
import { equalCustomizer } from 'utils/form';
import { transformNameInModelIntoFormName } from 'utils/formGenerator';
import { checkIsPurchaseFlow } from 'utils/loanApplication';
import { roundToDecimals } from 'utils/number';

import { openConfirmationDialogAction, showMessageAction } from 'store/global/actions';

import {
  ChecklistSortField,
  ChecklistStatusPriority,
  DEFAULT_CHECKLIST_STATUS_PRIORITY,
  LEAD_STAGE_STATUSES,
  LEASE_RENT_RATIO,
  PERCENT_MULTIPLIER,
  RETRY_MESSAGE_FALLBACK,
} from './LoanApplication.constants';
import { mortgageFieldNames } from './LoanApplication.sharedConfigs';
import { checklistRecalculationIconAnimationStyles } from './LoanApplication.sharedStyles';
import RetryActionPopup from './Validation/RetryActionPopup';
import UpdateProgramPopup from './Validation/UpdateProgramPopup';

export const getFormFieldsValuesByGroupName = (form, groupName) => {
  const filteredForm = _.pickBy(form, (_, key) => key.startsWith(groupName));

  return _.mapKeys(filteredForm, (_, key) => key.replace(groupName, ''));
};

const resolveTabCustomName = (tab, props) => {
  if (tab.getCustomName) {
    return { ...tab, customName: tab.getCustomName(props) };
  }

  return tab;
};

const getTabsWithPermissions = (config, props) => {
  return Object.entries(config).reduce((acc, [key, tab]) => {
    const isTabAvailable = !tab.getIsAvailable || tab.getIsAvailable(props);

    if (!isTabAvailable) {
      return acc;
    }

    const resolvedTab = resolveTabCustomName(tab, props);

    if (resolvedTab.subtabs) {
      const availableSubtabs = Object.entries(resolvedTab.subtabs).reduce(
        (subtabAcc, [subtabKey, subtab]) => {
          const isSubtabAvailable = !subtab.getIsAvailable || subtab.getIsAvailable(props);

          if (isSubtabAvailable) {
            subtabAcc[subtabKey] = resolveTabCustomName(subtab, props);
          }

          return subtabAcc;
        },
        {},
      );

      acc[key] = { ...resolvedTab, subtabs: availableSubtabs };
    }

    if (resolvedTab.injectSubtabs) {
      acc[key] = { ...resolvedTab, subtabs: resolvedTab.injectSubtabs(props) };
    }

    if (!resolvedTab.subtabs && !resolvedTab.injectSubtabs) {
      acc[key] = resolvedTab;
    }

    return acc;
  }, {});
};

export const getAvailableTabs = props => getTabsWithPermissions(LoanNavigationConfig, props);

export const checkIsLineOfCreditOrMortgageTypeOfCredit = accountType =>
  [TypeOfCredit.LINE_OF_CREDIT, TypeOfCredit.MORTGAGE].includes(accountType);

export const checkIsRetainedPropertyStatus = status => status === PropertyStatusType.RETAINED;

export const checkIsSoldPropertyStatus = status => status === PropertyStatusType.SOLD;

export const formatToPercents = (value, isWithoutSign) =>
  `${((value || 0) * PERCENT_MULTIPLIER).toFixed(2)}${isWithoutSign ? '' : '%'}`;

const getBaseExpenses = ({
  isSubjectProperty,
  taxesEscrowed,
  homeownersInsuranceEscrowed,
  monthlyPropertyTaxes,
  monthlyHomeownersInsurance,
  monthlyPmi,
}) => {
  if (isSubjectProperty || (!taxesEscrowed && !homeownersInsuranceEscrowed)) {
    return (
      (Number(monthlyPropertyTaxes) || 0) +
      (Number(monthlyHomeownersInsurance) || 0) +
      (Number(monthlyPmi) || 0)
    );
  }

  if (taxesEscrowed && !homeownersInsuranceEscrowed) {
    return (Number(monthlyHomeownersInsurance) || 0) + (Number(monthlyPmi) || 0);
  }

  if (!taxesEscrowed && homeownersInsuranceEscrowed) {
    return Number(monthlyPropertyTaxes) || 0;
  }

  return 0;
};

export const getExpenses = property => {
  const {
    monthlyPropertyTaxes,
    monthlyHomeownersInsurance,
    monthlyPmi,
    hasOtherExpenses,
    otherExpenses,
    associationFees,
    associationFeesAmount,
    isSubjectProperty,
    supplementalInsurances,

    // REO only
    taxesEscrowed,
    homeownersInsuranceEscrowed,
  } = property || {};

  const baseExpenses = getBaseExpenses({
    isSubjectProperty,
    taxesEscrowed,
    homeownersInsuranceEscrowed,
    monthlyPropertyTaxes,
    monthlyHomeownersInsurance,
    monthlyPmi,
  });

  const associationExpenses = associationFees ? Number(associationFeesAmount) || 0 : 0;

  const otherExpensesTotal = hasOtherExpenses
    ? (otherExpenses || []).reduce((sum, { value }) => sum + (Number(value) || 0), 0)
    : 0;

  const supplementalInsurancesTotal = _.reduce(
    supplementalInsurances,
    (sum, { monthlyAmount, isDeleted }) => (isDeleted ? sum : sum + (Number(monthlyAmount) || 0)),
    0,
  );

  return roundToDecimals(
    baseExpenses + associationExpenses + otherExpensesTotal + supplementalInsurancesTotal,
  );
};

export const getLiabilitiesPaymentsSum = liabilities =>
  liabilities.reduce((acc, liability) => {
    const { excludePayment, toBePaidOffAtOrBeforeClosing, monthlyPayment } = liability || {};

    const isMonthlyPaymentApplicable = !excludePayment && !toBePaidOffAtOrBeforeClosing;

    if (isMonthlyPaymentApplicable) {
      acc += Number(monthlyPayment) || 0;
    }

    return acc;
  }, 0);

export const getNetMonthlyRentalIncome = ({
  monthlyRentalIncome,
  expenses,
  isSubjectProperty,
  loanPurpose,
  principalAndInterest,
  liabilities,
  rentCalculatedBasedOn,
  status,
  occupancy,
  propertyType,
}) => {
  const isPurchaseFlow = checkIsPurchaseFlow(loanPurpose);
  const isPurchaseSubjectProperty = isSubjectProperty && isPurchaseFlow;
  const isPrimaryMultiUnitProperty =
    propertyType === PropertyType.MULTI_UNIT && occupancy === OccupancyType.PRIMARY_RESIDENCE;
  const isSoldPropertyStatus = checkIsSoldPropertyStatus(status);

  const ratio = rentCalculatedBasedOn === RentCalculatedPurposeType.LEASE ? LEASE_RENT_RATIO : 1;

  if (isSoldPropertyStatus && !isPurchaseSubjectProperty) {
    return 0;
  }

  if (isPrimaryMultiUnitProperty) {
    return ratio * (Number(monthlyRentalIncome) || 0);
  }

  const sumOfMonthlyPayment = getLiabilitiesPaymentsSum(liabilities);

  const monthlyPayments = isSubjectProperty ? principalAndInterest : sumOfMonthlyPayment;

  const netRent =
    ratio * (Number(monthlyRentalIncome) || 0) -
    (Number(monthlyPayments) || 0) -
    (Number(expenses) || 0);

  if (occupancy !== OccupancyType.PRIMARY_RESIDENCE) {
    return netRent;
  }

  return 0;
};

export const getFieldFormNameWithIndex = (index, { nameInModel }) =>
  transformNameInModelIntoFormName(nameInModel.replace('[0]', `[${index}]`));

export const handleLoanFeesError = ({
  response,
  onRetryAction,
  dispatch,
  getCustomErrorMessage,
}) => {
  const isLoanFeesErrorResponse = response?.status === HTTP_RESPONSE_STATUS_CODES.LOAN_FEES_ERROR;

  if (isLoanFeesErrorResponse) {
    const retryMessage = response?.data?.message ?? RETRY_MESSAGE_FALLBACK;

    return dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.ERROR,
        message: retryMessage,
        body: <RetryActionPopup message={retryMessage} onRetryAction={onRetryAction} />,
      }),
    );
  }

  return dispatch(
    showMessageAction({
      message: getCustomErrorMessage?.({ response }) || getDefaultErrorMessage({ response }),
      variant: 'error',
    }),
  );
};

export const checkIsInvalidProgramStatus = ({ response }) => {
  return response?.data?.type === CONFLICT_TYPE.INVALID_LOAN_PROGRAM;
};

export const handleNotAcceptableResponse = props => {
  const {
    response,
    actionData,
    dispatch,
    onAcceptAction,
    onAcceptFromProps,
    isSuccessActionNeededAfterAccept,
    isSaveWithoutProgramHidden,
    isSaveWithoutProgramNeededOnNoPrograms,
    isRevertNeededAfterAccept,
    successMessage,
    customErrorMessageGetter = _.noop,
    customErrorMessageDuration,
    isInvalidProgramFromProps,
    isRetryActionNeededFromProps,
    onRevert = _.noop,
    onCancel = _.noop,
    loanOptionsSource,
  } = props;

  const isInvalidProgram = isInvalidProgramFromProps || checkIsInvalidProgramStatus({ response });

  const isRetryActionNeededResponse =
    isRetryActionNeededFromProps || response?.status === HTTP_RESPONSE_STATUS_CODES.LOAN_FEES_ERROR;

  const onAccept = ({ ...props }) => {
    return dispatch(onAcceptAction({ ...actionData, ...props })).then(response => {
      if (successMessage) {
        dispatch(showMessageAction({ message: successMessage, variant: 'success' }));
      }

      return response;
    });
  };

  const { message } = response?.data || {};

  if (isInvalidProgram) {
    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.ERROR,
        message,
        body: (
          <UpdateProgramPopup
            handlerProps={props}
            isRevertNeededAfterAccept={isRevertNeededAfterAccept}
            isSaveWithoutProgramHidden={isSaveWithoutProgramHidden}
            isSaveWithoutProgramNeededOnNoPrograms={isSaveWithoutProgramNeededOnNoPrograms}
            isSuccessActionNeededAfterAccept={isSuccessActionNeededAfterAccept}
            loanOptionsSource={loanOptionsSource}
            message={message}
            onAccept={onAcceptFromProps || onAccept}
            onCancel={onCancel}
            onRevert={onRevert}
          />
        ),
      }),
    );

    return;
  }

  if (isRetryActionNeededResponse) {
    const retryMessage = message ?? RETRY_MESSAGE_FALLBACK;

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.ERROR,
        message: retryMessage,
        body: (
          <RetryActionPopup
            catchHandlerProps={props}
            message={retryMessage}
            onCatch={handleNotAcceptableResponse}
            onRetryAction={onAcceptFromProps || onAccept}
          />
        ),
      }),
    );

    return;
  }

  if (!isRetryActionNeededResponse) {
    dispatch(
      showMessageAction({
        variant: 'error',
        message: customErrorMessageGetter(response) || getDefaultErrorMessage({ response }),
        duration: customErrorMessageDuration,
      }),
    );
  }
};

export const checkIsAmountOfRentCollectedActive = ({ occupancy, propertyType }) => {
  const isInvestmentProperty = occupancy === OccupancyType.INVESTMENT_PROPERTY;
  const isPrimaryMultiUnitProperty =
    propertyType === PropertyType.MULTI_UNIT && OccupancyType.PRIMARY_RESIDENCE === occupancy;

  return isPrimaryMultiUnitProperty || isInvestmentProperty;
};

export const shouldResetCountyField = ({ counties, formState, formCounty }) =>
  formState &&
  !counties[formState]?.find(({ value }) => value?.toLowerCase() === formCounty?.toLowerCase());

export const getFormattedFullAddress = ({ address, propertyType }) => {
  const isMultiUnit = propertyType === PropertyType.MULTI_UNIT;

  const { street, unit, city, state, zip } = address || {};
  const correctedUnit = !isMultiUnit && unit && `Unit ${unit}`;
  const correctedState = state ? `${state} ` : '';
  const stateAndZip = `${correctedState}${zip || ''}`;

  return [street, correctedUnit, city, stateAndZip].filter(Boolean).join(', ');
};

export const getImpersonatedByUserLabel = ({
  userProp,
  fallbackUserProp,
  impersonatedUserProp,
}) => {
  if (_.isNil(userProp)) {
    return fallbackUserProp || '';
  }

  const isImpersonationExist = userProp && impersonatedUserProp;

  return isImpersonationExist ? `${userProp}\nimpersonated by ${impersonatedUserProp}` : userProp;
};

export const checkIsApplicationAtLeadStage = applicationStatus =>
  LEAD_STAGE_STATUSES.includes(applicationStatus);

export const getFormLiabilities = form =>
  getConditionalArray(form, () => [
    ...[
      form?.hasFirstMortgage
        ? mortgageFieldNames.reduce((acc, fieldName) => {
            acc[fieldName] = form[`${PropertyMortgageType.FIRST}${fieldName}`];

            return acc;
          }, {})
        : {},
    ],

    ...[
      form?.hasSecondMortgage
        ? mortgageFieldNames.reduce((acc, fieldName) => {
            acc[fieldName] = form[`${PropertyMortgageType.SECOND}${fieldName}`];

            return acc;
          }, {})
        : {},
    ],
  ]);

export const deepObjFind = (obj, query) => {
  const keys = Object.keys(obj);
  const directMatch = keys.find(key => key === query);

  if (directMatch) {
    return directMatch;
  }

  return keys.reduce((found, key) => {
    if (found) {
      return found;
    }

    if (typeof obj[key] === 'object' && obj[key] !== null) {
      return deepObjFind(obj[key], query);
    }

    return null;
  }, null);
};

export const getIsEmptyObj = (obj, { isFalsyCheck = false } = {}) =>
  obj ? Object.values(obj).every(isFalsyCheck ? value => !value : _.isEmpty) : false;

export const getSubtabsFromChecklistResults = (
  checkListResults,
  { isRecalculationHidden = false } = {},
) => {
  const subtabsArray = checkListResults
    .map(({ status, target, checklist }) => {
      if (!status?.result && !checklist) {
        return null;
      }

      // A hidden checklist is a completeness decisioning engine (DEV-4352): it only drives the
      // in-editor "Needed" highlighting and must never surface as an Alerts subtab (DEV-5692).
      const checklistMeta = status?.result?.checklist ?? checklist;

      if (checklistMeta?.isHidden) {
        return null;
      }

      const isInProgress =
        !isRecalculationHidden && CHECKLIST_RECALCULATING_STATUSES.includes(status?.type);

      const { iconType: sortStatusType } = getChecklistResultStatus({
        state: status?.result?.calculatedState,
        result: status?.result?.calculatedResult,
        statusDisplayType: status?.result?.checklist?.displaySettings?.statusDisplayType,
      });
      const statusIconType = isInProgress ? StatusEnum.IN_PROGRESS : sortStatusType;

      return {
        checklistId: target?.checklistId,
        tabName: status?.result?.checklist?.name || checklist?.name,
        tabRoute: target?.checklistId,
        statusIconType,
        statusPriority:
          ChecklistStatusPriority[sortStatusType] ?? DEFAULT_CHECKLIST_STATUS_PRIORITY,
        isInProgress,
      };
    })
    .filter(Boolean);

  const sortedSubtabs = _.orderBy(
    subtabsArray,
    [ChecklistSortField.STATUS_PRIORITY, ChecklistSortField.TAB_NAME],
    [SortOrder.ASC, SortOrder.ASC],
  );

  return sortedSubtabs.reduce(
    (acc, { checklistId, tabName, tabRoute, isInProgress, statusIconType }) => {
      acc[checklistId] = {
        tabName,
        tabRoute,
        alternativeIconNode: (
          <StatusIconCDS
            size={IconSize[18]}
            sx={getConditionalObject(isInProgress, checklistRecalculationIconAnimationStyles)}
            type={statusIconType}
          />
        ),
      };

      return acc;
    },
    {},
  );
};

export const getCommonDate = date => date && dayjs(date).format(COMMON_DATE_FORMAT);

export const getPascalCase = str => {
  if (!str) {
    return '';
  }

  const camelCase = _.camelCase(str);

  return camelCase.charAt(0).toUpperCase() + camelCase.slice(1);
};

export const getCurrentEntityPendingChanges = ({
  currentValues,
  initialValues = {},
  actionType,
  omittedFields = [],
}) => {
  const { isAdded, isDeleted } = currentValues;

  return Object.entries(currentValues).reduce(
    (acc, [key, value]) => {
      if (omittedFields.includes(key)) {
        return acc;
      }

      if (isAdded) {
        acc[key] = {
          newValue: value,
          initialValue: null,
          actionType: HistoryPendingChangesActionType.ADDED,
        };

        return acc;
      }

      const initialValue = initialValues[key];
      const isValuesEqual = _.isEqualWith(value, initialValue, equalCustomizer);

      if (isValuesEqual) {
        return acc;
      }

      if (isDeleted) {
        acc[key] = {
          newValue: value,
          initialValue,
          actionType: HistoryPendingChangesActionType.DELETED,
        };

        return acc;
      }

      acc[key] = {
        newValue: value,
        initialValue,
        actionType: actionType ?? HistoryPendingChangesActionType.MODIFIED,
      };

      return acc;
    },
    { isSeparateChange: true },
  );
};
